// src/shared/components/FeatureRequestsViewer.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { useAppSelector } from '@app/store/hooks';
import Modal from '@shared/components/Modal';
import Input from '@shared/components/Input';
import Textarea from '@shared/components/Textarea';
import {
  type FeatureRequest,
  type FeatureRequestStatus,
  getFeatureRequests,
  createFeatureRequest,
  updateFeatureRequest,
  updateFeatureRequestStatus,
  deleteFeatureRequest,
  replyToFeatureRequest,
} from '@shared/services/featureRequestService';

const statusLabels: Record<FeatureRequestStatus, string> = {
  pending: 'در انتظار',
  in_progress: 'در حال بررسی',
  done: 'انجام شده',
  rejected: 'رد شده',
};

const statusColors: Record<FeatureRequestStatus, string> = {
  pending: 'bg-slate-100 text-slate-800 border-slate-200',
  in_progress: 'bg-purple-100 text-purple-800 border-purple-200',
  done: 'bg-green-100 text-green-800 border-green-200',
  rejected: 'bg-gray-100 text-gray-600 border-gray-200',
};

function isRouteNotFound(error: any): boolean {
  const msg = String(error?.response?.data?.message ?? error?.message ?? '');
  return error?.response?.status === 404 || /could not be found|route.*not found/i.test(msg);
}

function formatPersianDate(value?: string): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('fa-IR', { year: 'numeric', month: 'long', day: 'numeric' });
}

interface FeatureRequestsViewerProps {
  logsEnabled?: boolean;
  bugsEnabled?: boolean;
}

const FeatureRequestsViewer: React.FC<FeatureRequestsViewerProps> = ({ logsEnabled = false, bugsEnabled = false }) => {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const [items, setItems] = useState<FeatureRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isListOpen, setIsListOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editItem, setEditItem] = useState<FeatureRequest | null>(null);
  const [statusFilter, setStatusFilter] = useState<FeatureRequestStatus | 'all'>('all');
  const [query, setQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | number | null>(null);
  const [deletingId, setDeletingId] = useState<string | number | null>(null);
  const [deleteConfirmItem, setDeleteConfirmItem] = useState<FeatureRequest | null>(null);
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({});
  const [replyingId, setReplyingId] = useState<string | number | null>(null);

  // Position to the right of Bugs (and Logs): avoid overlapping the bugs button
  const desktopLeftClass =
    logsEnabled && bugsEnabled
      ? 'sm:left-72'        // 18rem: after Logs (left-4) + Bugs button (left-24 + width)
      : bugsEnabled
        ? 'sm:left-[11.25rem]' // only Bugs: a small visual gap after the bug button
        : logsEnabled
          ? 'sm:left-24'    // only Logs: same as bugs position
          : 'sm:left-4';

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await getFeatureRequests();
      setItems(list);
    } catch (e: any) {
      if (isRouteNotFound(e)) {
        setItems([]);
        setError(null);
      } else {
        setError(e?.message || 'خطا در بارگذاری درخواست‌ها');
        setItems([]);
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isListOpen) refresh();
  }, [isListOpen, refresh]);

  useEffect(() => {
    if (isAuthenticated && !isListOpen) refresh();
  }, [isAuthenticated]);

  const addItem = async (data: { pageName: string; title: string; description: string; steps?: string; url?: string }) => {
    try {
      await createFeatureRequest({
        page_name: data.pageName,
        title: data.title,
        description: data.description,
        steps: data.steps,
        url: data.url,
      });
      toast.success('درخواست ویژگی ثبت شد');
      setIsAddOpen(false);
      await refresh();
    } catch (e: any) {
      toast.error(e?.message || 'خطا در ثبت درخواست');
    }
  };

  const updateItem = async (
    id: string | number,
    data: { pageName: string; title: string; description: string; steps?: string; url?: string }
  ) => {
    try {
      await updateFeatureRequest(id, {
        page_name: data.pageName,
        title: data.title,
        description: data.description,
        steps: data.steps,
        url: data.url,
      });
      toast.success('درخواست به‌روزرسانی شد');
      setEditItem(null);
      await refresh();
    } catch (e: any) {
      toast.error(e?.message || 'خطا در به‌روزرسانی');
    }
  };

  const updateStatus = async (id: string | number, status: FeatureRequestStatus) => {
    try {
      await updateFeatureRequestStatus(id, status);
      setItems((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch (e: any) {
      toast.error(e?.message || 'خطا در به‌روزرسانی وضعیت');
    }
  };

  const sendReply = async (id: string | number) => {
    const key = String(id);
    const body = (replyDrafts[key] ?? '').trim();
    if (!body) return;

    setReplyingId(id);
    try {
      const updated = await replyToFeatureRequest(id, body);
      setItems((prev) => prev.map((item) => (item.id === id ? updated : item)));
      setReplyDrafts((prev) => ({ ...prev, [key]: '' }));
      toast.success('پاسخ ثبت شد');
    } catch (e: any) {
      toast.error(e?.message || 'خطا در ثبت پاسخ');
    } finally {
      setReplyingId(null);
    }
  };

  const openDeleteConfirm = (item: FeatureRequest) => setDeleteConfirmItem(item);

  const removeItem = async (id: string | number) => {
    setDeleteConfirmItem(null);
    setDeletingId(id);
    try {
      await deleteFeatureRequest(id);
      setItems((prev) => prev.filter((r) => r.id !== id));
      toast.success('درخواست حذف شد');
    } catch (e: any) {
      toast.error(e?.message || 'خطا در حذف');
    } finally {
      setDeletingId(null);
    }
  };

  const filteredItems = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase('fa-IR');
    return items.filter((item) => {
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      const searchable = `${item.title} ${item.description} ${item.pageName} ${item.submittedBy.name}`.toLocaleLowerCase('fa-IR');
      return matchesStatus && (!normalizedQuery || searchable.includes(normalizedQuery));
    });
  }, [items, query, statusFilter]);

  const statusCounts = useMemo(
    () =>
      (Object.keys(statusLabels) as FeatureRequestStatus[]).reduce<Record<FeatureRequestStatus, number>>(
        (counts, status) => ({ ...counts, [status]: items.filter((item) => item.status === status).length }),
        { pending: 0, in_progress: 0, done: 0, rejected: 0 }
      ),
    [items]
  );

  if (!isListOpen) {
    return (
      <button
        type="button"
        onClick={() => {
          setIsListOpen(true);
        }}
        className={`fixed bottom-3 left-3 right-3 z-40 flex h-12 items-center justify-center gap-2 rounded-2xl border border-white/20 bg-gradient-to-l from-emerald-600 via-green-600 to-teal-600 px-4 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(16,185,129,0.28)] transition hover:-translate-y-0.5 hover:shadow-[0_16px_36px_rgba(16,185,129,0.34)] disabled:cursor-wait disabled:opacity-80 ${desktopLeftClass} sm:bottom-4 sm:right-auto sm:w-auto sm:min-w-[164px]`}
        title="درخواست ویژگی"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 18h6M10 22h4M8.25 14.5a7 7 0 1110.5 0c-.92.77-1.5 1.92-1.5 3.25H9.75c0-1.33-.58-2.48-1.5-3.25zM12 4v1.5M5.64 6.64l1.06 1.06M18.36 6.64L17.3 7.7M4 12h1.5M18.5 12H20" />
        </svg>
        <span>درخواست ویژگی</span>
        <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">{loading ? '…' : items.length}</span>
      </button>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 z-[40] flex items-end justify-center bg-slate-950/45 p-0 backdrop-blur-[2px] sm:items-center sm:p-6"
        onClick={() => setIsListOpen(false)}
      >
        <motion.section
          initial={{ opacity: 0, y: 32, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: 'spring', damping: 26, stiffness: 260 }}
          onClick={(event) => event.stopPropagation()}
          dir="rtl"
          className="flex h-[100dvh] w-full max-w-[1680px] flex-col overflow-hidden bg-slate-50 shadow-2xl sm:h-[96vh] sm:rounded-xl sm:border sm:border-slate-200"
          aria-label="مرکز درخواست‌های ویژگی"
        >
          <header className="relative shrink-0 border-b-2 border-violet-500 bg-white px-4 py-3 text-slate-900 sm:px-6">
            <div className="relative flex items-center justify-between gap-4">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-violet-200 bg-violet-50 text-violet-700 sm:h-11 sm:w-11">
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <h2 className="truncate text-base font-bold sm:text-lg">درخواست‌های ویژگی</h2>
                  <p className="mt-0.5 text-xs text-slate-500">ثبت پیشنهادها و پیگیری روند بررسی آن‌ها</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsListOpen(false)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 hover:text-slate-800"
                aria-label="بستن"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </header>

          <div className="shrink-0 border-b border-slate-200 bg-white px-3 py-2.5 sm:px-6 sm:py-3">
            <div className="flex flex-col gap-1.5 sm:flex-row sm:gap-2">
              {(Object.keys(statusLabels) as FeatureRequestStatus[]).map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
                  className={`flex flex-1 items-center justify-between rounded-md border px-3 py-1.5 text-right transition sm:px-3 sm:py-1.5 ${
                    statusFilter === status ? statusColors[status] : 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-slate-100'
                  }`}
                >
                  <span className="text-xs font-medium sm:text-sm">{statusLabels[status]}</span>
                  <span className="text-base font-bold sm:text-lg">{statusCounts[status]}</span>
                </button>
              ))}
            </div>

            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
              <label className="relative min-w-0 flex-1">
                <span className="sr-only">جست‌وجوی درخواست‌ها</span>
                <svg className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="m21 21-4.35-4.35m1.35-5.65a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="جست‌وجو در عنوان، توضیحات، صفحه یا ثبت‌کننده…"
                  className="h-11 w-full rounded-xl border border-slate-200 bg-slate-50 pr-10 pl-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-violet-400 focus:bg-white focus:ring-4 focus:ring-violet-100"
                />
              </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as FeatureRequestStatus | 'all')}
                className="h-11 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-violet-400 focus:ring-4 focus:ring-violet-100 sm:w-40"
            >
                <option value="all">همه وضعیت‌ها</option>
              {(Object.keys(statusLabels) as FeatureRequestStatus[]).map((s) => (
                <option key={s} value={s}>
                  {statusLabels[s]}
                </option>
              ))}
            </select>
            <button
                type="button"
              onClick={() => setIsAddOpen(true)}
                className="flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 sm:min-w-[172px]"
            >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.5v15m7.5-7.5h-15" /></svg>
                ثبت درخواست جدید
            </button>
          </div>
          </div>

          <main className="min-h-0 flex-1 overflow-y-auto px-3 py-2 sm:px-6 sm:py-3">
          {!isAuthenticated && (
              <div className="flex min-h-64 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white text-center text-slate-500">برای مشاهده و ثبت درخواست ویژگی وارد شوید.</div>
          )}
          {isAuthenticated && error && (
              <div className="flex min-h-64 flex-col items-center justify-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 text-center text-sm text-red-700">
                <span>{error}</span>
                <button type="button" onClick={refresh} className="rounded-xl bg-red-600 px-4 py-2 font-medium text-white hover:bg-red-700">تلاش دوباره</button>
              </div>
          )}
            {isAuthenticated && !error && loading && (
              <div className="space-y-2">
                {[0, 1, 2, 3].map((item) => <div key={item} className="h-20 animate-pulse rounded-lg border border-slate-200 bg-white p-3"><div className="h-5 w-24 rounded bg-slate-200" /><div className="mt-3 h-4 w-2/3 rounded bg-slate-200" /></div>)}
              </div>
            )}
            {isAuthenticated && !error && !loading && filteredItems.length === 0 && (
              <div className="flex min-h-64 flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white px-4 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-50 text-violet-600">
                  <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.7} d="M9.75 9.75h.008v.008H9.75V9.75zm4.5 0h.008v.008h-.008V9.75zM9.5 15.25a3.5 3.5 0 015 0M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <h3 className="mt-3 font-semibold text-slate-800">درخواستی پیدا نشد</h3>
                <p className="mt-1 text-sm text-slate-500">فیلتر یا عبارت جست‌وجو را تغییر دهید.</p>
              </div>
            )}
            {isAuthenticated && !error && !loading && filteredItems.length > 0 && (
              <div className="space-y-2">
                <div className="hidden grid-cols-[25rem_minmax(10rem,1fr)_15rem_19rem] items-center gap-x-3 rounded-md border border-slate-200 bg-slate-100 px-3 py-2 text-right text-[11px] font-semibold text-slate-500 xl:grid">
                  <div>وضعیت و عنوان</div>
                  <div>توضیحات</div>
                  <div>ثبت‌کننده و صفحه</div>
                  <div className="text-left">عملیات</div>
                </div>
                {filteredItems.map((item) => {
                  const isExpanded = expandedId === item.id;
                  return (
                    <article key={item.id} className="group flex flex-col rounded-md border border-slate-200 bg-white p-2.5 text-right shadow-sm transition hover:border-violet-300 hover:bg-violet-50/30 xl:grid xl:grid-cols-[25rem_minmax(10rem,1fr)_15rem_19rem] xl:items-center xl:gap-x-3 xl:px-3 xl:py-2">
                      <div className="flex min-w-0 items-center justify-between gap-3 xl:col-start-1 xl:row-start-1">
                        <div className="min-w-0 xl:flex xl:items-center xl:gap-2">
                          <span className={`inline-flex shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${statusColors[item.status]}`}>{statusLabels[item.status]}</span>
                          {item.replies.length > 0 && (
                            <span className="inline-flex shrink-0 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                              پاسخ داده شده
                            </span>
                          )}
                          <h3 className="mt-1 break-words text-sm font-bold leading-6 text-slate-900 xl:mt-0 xl:truncate xl:text-base">{item.title || '(بدون عنوان)'}</h3>
                        </div>
                        <span className="shrink-0 whitespace-nowrap text-xs text-slate-400">{formatPersianDate(item.createdAt)}</span>
                      </div>

                      <p className={`mt-1 whitespace-pre-wrap break-words text-xs leading-5 text-slate-600 xl:col-start-2 xl:row-start-1 xl:mt-0 ${isExpanded ? '' : 'max-h-5 overflow-hidden xl:min-w-0 xl:truncate'}`}>{item.description}</p>

                      <div className="mt-2 flex min-w-0 flex-wrap gap-1.5 text-xs text-slate-500 xl:col-start-3 xl:row-start-1 xl:mt-0 xl:flex-nowrap">
                        <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded border border-slate-200 bg-white px-2 py-1">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.5 20.12a7.5 7.5 0 0115 0A17.93 17.93 0 0112 21.75c-2.68 0-5.22-.59-7.5-1.63z" /></svg>
                          {item.submittedBy.name}
                        </span>
                        <span className="inline-flex min-w-0 flex-1 items-center gap-1 whitespace-nowrap rounded border border-slate-200 bg-white px-2 py-1 font-mono" title={item.pageName}>
                          <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M17.25 6.75L21 10.5l-3.75 3.75M6.75 17.25L3 13.5l3.75-3.75M14.25 4.5l-4.5 15" /></svg>
                          <span className="max-w-[220px] truncate">{item.pageName || 'صفحه نامشخص'}</span>
                        </span>
                      </div>

                      {isExpanded && (
                        <div className="mt-3 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm xl:col-span-4 xl:row-start-2">
                          {item.steps && (
                            <div><p className="mb-2 font-semibold text-slate-700">جزئیات درخواست</p><ol className="space-y-1.5 text-slate-600">{item.steps.split(/\n/).filter(Boolean).map((step, index) => <li key={`${item.id}-${index}`}>{index + 1}. {step}</li>)}</ol></div>
                          )}
                          {item.url && /^https?:\/\//i.test(item.url) && (
                            <a href={item.url} target="_blank" rel="noreferrer" className="inline-flex max-w-full items-center gap-1 text-xs font-medium text-violet-700 hover:text-violet-800"><span className="truncate">باز کردن آدرس ثبت‌شده</span><span aria-hidden="true">↗</span></a>
                          )}
                          <div className="border-t border-slate-200 pt-3">
                            <div className="mb-2 flex items-center justify-between gap-2">
                              <p className="font-semibold text-slate-700">پاسخ‌ها</p>
                              <span className="text-xs text-slate-400">{item.replies.length} پاسخ</span>
                            </div>
                            {item.replies.length > 0 && (
                              <div className="mb-3 space-y-2">
                                {item.replies.map((reply) => (
                                  <div key={reply.id} className="rounded-md border border-violet-100 bg-white px-3 py-2">
                                    <div className="mb-1 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
                                      <span className="font-semibold text-violet-700">{reply.authorName}</span>
                                      <span>{formatPersianDate(reply.createdAt)}</span>
                                    </div>
                                    <p className="whitespace-pre-wrap break-words text-sm leading-6 text-slate-700">{reply.body}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                              <textarea
                                value={replyDrafts[String(item.id)] ?? ''}
                                onChange={(event) => setReplyDrafts((prev) => ({ ...prev, [String(item.id)]: event.target.value }))}
                                placeholder="پاسخ خود را بنویسید…"
                                rows={2}
                                maxLength={5000}
                                className="min-h-[64px] flex-1 resize-y rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-violet-400 focus:ring-2 focus:ring-violet-100"
                              />
                              <button
                                type="button"
                                onClick={() => sendReply(item.id)}
                                disabled={replyingId === item.id || !(replyDrafts[String(item.id)] ?? '').trim()}
                                className="h-10 shrink-0 rounded-md bg-violet-600 px-4 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {replyingId === item.id ? 'در حال ثبت…' : 'ثبت پاسخ'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="mt-2 flex flex-col gap-2 border-t border-slate-100 pt-2 xl:col-start-4 xl:row-start-1 xl:mt-0 xl:w-auto xl:flex-row xl:items-center xl:justify-between xl:border-t-0 xl:pt-0">
                        <button type="button" onClick={() => setExpandedId(isExpanded ? null : item.id)} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-violet-700" aria-expanded={isExpanded}>
                          {isExpanded ? 'بستن جزئیات' : 'مشاهده جزئیات'}
                          <svg className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m19.5 8.25-7.5 7.5-7.5-7.5" /></svg>
                        </button>
                        <div className="flex items-center gap-2">
                          <select value={item.status} onChange={(event) => updateStatus(item.id, event.target.value as FeatureRequestStatus)} className="h-8 min-w-0 flex-1 rounded border border-slate-200 bg-white px-2 text-xs text-slate-700 outline-none focus:border-violet-400 sm:flex-none">{(Object.keys(statusLabels) as FeatureRequestStatus[]).map((status) => <option key={status} value={status}>{statusLabels[status]}</option>)}</select>
                          {item.isOwner && (
                            <>
                            <button type="button" onClick={() => setEditItem(item)} className="h-8 rounded border border-slate-200 px-2.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50">ویرایش</button>
                            <button type="button" onClick={() => openDeleteConfirm(item)} disabled={deletingId === item.id} className="h-8 rounded border border-red-200 px-2.5 text-xs font-medium text-red-600 transition hover:bg-red-50 disabled:opacity-50">{deletingId === item.id ? '…' : 'حذف'}</button>
                            </>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
          )}
          </main>
        </motion.section>
      </motion.div>

      <ReportFeatureRequestModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onSubmit={addItem}
      />
      <ReportFeatureRequestModal
        isOpen={editItem !== null}
        onClose={() => setEditItem(null)}
        onSubmit={(data) => editItem && updateItem(editItem.id, data)}
        initialItem={editItem ?? undefined}
        title="ویرایش درخواست ویژگی"
      />

      <Modal
        isOpen={!!deleteConfirmItem}
        onClose={() => setDeleteConfirmItem(null)}
        title="تأیید حذف"
        size="sm"
        showCloseButton={true}
        footer={
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => setDeleteConfirmItem(null)}
              className="text-sm px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={() => deleteConfirmItem && removeItem(deleteConfirmItem.id)}
              disabled={deletingId === deleteConfirmItem?.id}
              className="text-sm px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {deletingId === deleteConfirmItem?.id ? 'در حال حذف...' : 'حذف'}
            </button>
          </div>
        }
      >
        {deleteConfirmItem && (
          <p className="text-gray-600">
            آیا از حذف درخواست «{deleteConfirmItem.title || '(بدون عنوان)'}» اطمینان دارید؟
          </p>
        )}
      </Modal>
    </>
  );
};

interface ReportFeatureRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (report: {
    pageName: string;
    title: string;
    description: string;
    steps?: string;
    url?: string;
  }) => void;
  initialItem?: FeatureRequest | null;
  title?: string;
}

const PAGE_NAME_MAX = 500;
const TITLE_MAX = 500;
const DESCRIPTION_MIN = 3;
const DESCRIPTION_MAX = 5000;

const ReportFeatureRequestModal: React.FC<ReportFeatureRequestModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  initialItem,
  title: titleProp,
}) => {
  const [pageName, setPageName] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState<string[]>(['']);
  const [errors, setErrors] = useState<{ pageName?: string; title?: string; description?: string }>({});

  useEffect(() => {
    if (isOpen) {
      if (initialItem) {
        setPageName(initialItem.pageName || '');
        setTitle(initialItem.title || '');
        setDescription(initialItem.description || '');
        setSteps(
          initialItem.steps && initialItem.steps.trim()
            ? initialItem.steps.trim().split(/\n/).filter(Boolean)
            : ['']
        );
      } else {
        setPageName(window.location.pathname || '');
        setTitle('');
        setDescription('');
        setSteps(['']);
      }
      setErrors({});
    }
  }, [isOpen, initialItem]);

  const setStepAt = (index: number, value: string) => {
    setSteps((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const addStep = () => setSteps((prev) => [...prev, '']);
  const removeStep = (index: number) => {
    if (steps.length <= 1) return;
    setSteps((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = (): boolean => {
    const next: typeof errors = {};
    const desc = description.trim();
    if (!desc) {
      next.description = 'توضیحات درخواست الزامی است.';
    } else if (desc.length < DESCRIPTION_MIN) {
      next.description = `حداقل ${DESCRIPTION_MIN} کاراکتر وارد کنید.`;
    } else if (desc.length > DESCRIPTION_MAX) {
      next.description = `حداکثر ${DESCRIPTION_MAX} کاراکتر مجاز است.`;
    }
    const page = pageName.trim();
    if (page.length > PAGE_NAME_MAX) {
      next.pageName = `حداکثر ${PAGE_NAME_MAX} کاراکتر مجاز است.`;
    }
    const t = title.trim();
    if (t.length > TITLE_MAX) {
      next.title = `حداکثر ${TITLE_MAX} کاراکتر مجاز است.`;
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const stepList = steps.map((s) => s.trim()).filter(Boolean);
    onSubmit({
      pageName: pageName.trim() || window.location.pathname || 'نامشخص',
      title: title.trim() || '(بدون عنوان)',
      description: description.trim(),
      steps: stepList.length > 0 ? stepList.join('\n') : undefined,
      url: window.location.href,
    });
  };

  const clearError = (field: keyof typeof errors) => {
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={titleProp ?? 'درخواست ویژگی جدید'}
      size="lg"
      footer={
        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 sm:w-auto"
          >
            انصراف
          </button>
          <button
            type="submit"
            form={initialItem ? 'edit-feature-form' : 'add-feature-form'}
            className="w-full rounded-xl bg-gradient-to-l from-violet-600 to-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:shadow-md sm:w-auto"
          >
            ذخیره
          </button>
        </div>
      }
    >
      <form
        id={initialItem ? 'edit-feature-form' : 'add-feature-form'}
        onSubmit={handleSubmit}
        noValidate
        className="space-y-5"
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
          <Input
            label="صفحه (اختیاری)"
            value={pageName}
            onChange={(e) => {
              setPageName(e.target.value);
              clearError('pageName');
            }}
            placeholder="/path/to/page"
            className="text-sm"
            error={errors.pageName}
            helperText={!errors.pageName ? 'مسیر صفحه در اپ؛ در صورت خالی بودن، مسیر فعلی ذخیره می‌شود.' : undefined}
          />
          </div>
          <div>
          <Input
            label="عنوان (اختیاری)"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              clearError('title');
            }}
            placeholder="خلاصه درخواست"
            className="text-sm"
            error={errors.title}
            helperText={!errors.title ? `حداکثر ${TITLE_MAX} کاراکتر.` : undefined}
          />
          </div>
        </div>
        <div>
          <Textarea
            label="توضیحات"
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              clearError('description');
            }}
            placeholder="شرح ویژگی مورد نظر را بنویسید"
            rows={4}
            className="text-sm"
            required
            error={errors.description}
            helperText={
              !errors.description
                ? `الزامی؛ حداقل ${DESCRIPTION_MIN} و حداکثر ${DESCRIPTION_MAX} کاراکتر.`
                : undefined
            }
          />
        </div>
        <div>
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
          <label className="mb-1 block text-sm font-semibold text-slate-700">مراحل یا جزئیات (اختیاری)</label>
          <p className="mb-3 text-xs text-slate-500">هر نکته را جداگانه ثبت کنید تا درخواست دقیق‌تر بررسی شود.</p>
          <div className="space-y-3">
            {steps.map((step, index) => (
              <div key={index} className="grid grid-cols-[1.75rem_minmax(0,1fr)_auto] items-center gap-2">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white text-xs font-semibold text-slate-500 shadow-sm">{index + 1}</span>
                <Input
                  value={step}
                  onChange={(e) => setStepAt(index, e.target.value)}
                  placeholder={index === 0 ? 'مثلاً: امکان فیلتر بر اساس تاریخ' : `مورد ${index + 1}`}
                  className="text-sm flex-1"
                />
                {steps.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeStep(index)}
                    className="shrink-0 rounded-lg border border-red-200 bg-white px-2.5 py-2 text-xs text-red-600 transition hover:bg-red-50"
                  >
                    حذف
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addStep}
              className="w-full rounded-xl border border-dashed border-violet-400 bg-white px-3 py-2 text-sm font-medium text-violet-700 transition hover:bg-violet-50 sm:w-auto"
            >
              + افزودن مورد
            </button>
          </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default FeatureRequestsViewer;
export type { FeatureRequest, FeatureRequestStatus } from '@shared/services/featureRequestService';
